import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { TRecommendation } from '../../types/recommendation';
import {
  getMobileRecommendations,
  getDesktopRecommendations,
  TRecommendationsResult,
} from '../../services/getRecommendations';
import { ELoadingStatus } from '../../types/loadingStatus';
import { createAppAsyncThunk } from '../../utils/redux/createAppAsyncThunk';

const INITIAL_PAGE = 0;

export enum EPaginationStatus {
  Idle = 'idle',
  Loading = 'loading',
  Error = 'error',
}

const initialState = {
  data: [] as TRecommendation[],
  status: ELoadingStatus.Loading,
  hasRequested: false,
  page: INITIAL_PAGE,
  hasMore: false,
  paginationStatus: EPaginationStatus.Idle,
};

export const fetchRecommendations = createAppAsyncThunk('recommendations/fetch', (_, { getState, extra: context }) => {
  const {
    custom: { isMobile },
  } = context;
  const { regionId, landingType, liquiditySource } = getState();
  const parameters = {
    regionId,
    landingType,
    ...(liquiditySource ? { liquiditySource } : null),
    ...(isMobile ? { page: INITIAL_PAGE } : null),
  };

  const getRecommendations = isMobile ? getMobileRecommendations : getDesktopRecommendations;

  return getRecommendations(context, { parameters, offset: INITIAL_PAGE });
});

type TFetchMoreRecommendationsPayload = TRecommendationsResult & {
  page: number;
};

export const fetchMoreRecommendations = createAppAsyncThunk(
  'recommendations/fetchMore',
  async (_, { getState, extra: context }) => {
    const {
      custom: { isMobile },
    } = context;
    const { regionId, landingType, liquiditySource, recommendations } = getState();

    if (!isMobile) {
      return {
        recommendations: [],
        hasMore: false,
        page: recommendations.page,
      } as TFetchMoreRecommendationsPayload;
    }

    const nextPage = recommendations.page + 1;
    const parameters = {
      regionId,
      landingType,
      page: nextPage,
      ...(liquiditySource ? { liquiditySource } : null),
    };
    const response = await getMobileRecommendations(context, {
      parameters,
      offset: recommendations.data.length,
    });

    return {
      ...response,
      page: nextPage,
    };
  },
);

type TToggleFavoritePayload = {
  offerId: number;
  isFavorite: boolean;
};

const recommendationsSlice = createSlice({
  name: 'recommendations',
  reducers: {
    toggleFavorite: (state, action: PayloadAction<TToggleFavoritePayload>) => {
      state.data.forEach(recommendation => {
        if (recommendation.offerId === action.payload.offerId) {
          recommendation.isFavorite = action.payload.isFavorite;
        }
      });
    },
  },
  initialState,
  extraReducers(builder) {
    builder.addCase(fetchRecommendations.pending, state => {
      state.status = ELoadingStatus.Loading;
      state.hasRequested = true;
      state.paginationStatus = EPaginationStatus.Idle;
    });
    builder.addCase(fetchRecommendations.fulfilled, (state, { payload }) => {
      state.status = ELoadingStatus.Success;
      state.data = payload.recommendations;
      state.page = INITIAL_PAGE;
      state.hasMore = payload.hasMore;
      state.paginationStatus = EPaginationStatus.Idle;
    });
    builder.addCase(fetchRecommendations.rejected, state => {
      state.status = ELoadingStatus.Failure;
      state.hasMore = false;
      state.page = INITIAL_PAGE;
      state.paginationStatus = EPaginationStatus.Idle;
    });
    builder.addCase(fetchMoreRecommendations.pending, state => {
      state.paginationStatus = EPaginationStatus.Loading;
    });
    builder.addCase(fetchMoreRecommendations.fulfilled, (state, { payload }) => {
      state.data = [...state.data, ...payload.recommendations];
      state.page = payload.page;
      state.hasMore = payload.hasMore;
      state.paginationStatus = EPaginationStatus.Idle;
    });
    builder.addCase(fetchMoreRecommendations.rejected, state => {
      state.paginationStatus = EPaginationStatus.Error;
    });
  },
});

export const recommendationsReducer = recommendationsSlice.reducer;
export const { toggleFavorite } = recommendationsSlice.actions;
